// src/utils/topicCategoryIconMap.ts
//
// Maps iconKey strings (stored in md_topic_category.icon_key) to react-icons components.
// This is a SEPARATE, larger icon set from roleIconMap.ts — person roles are mostly
// sports positions, but topic categories ("kanallar") cover general subjects like
// sinema, ekonomi, moda, teknoloji, etc., so a different (and much bigger) icon
// library is curated here to give a relevant match for every channel, with plenty
// of extra icons for channels that don't exist yet.
//
// Icons are grouped for the admin icon picker UI so browsing ~200 icons is
// manageable instead of one long unlabeled grid.
//
// To add a new icon: import it, add it to the relevant group below (or a new group).

import React from "react";
import {
  // Entertainment
  FaFilm, FaTv, FaMusic, FaGamepad, FaCameraRetro, FaTheaterMasks,
  FaMicrophoneAlt, FaCompactDisc, FaHeadphones, FaStar,
  FaPlayCircle, FaVideo, FaTicketAlt, FaDice, FaPuzzlePiece,
  FaYoutube, FaSpotify, FaMagic, FaGhost, FaMask, FaPaintBrush, FaGuitar, FaDrum, FaChess,
  // Knowledge & culture
  FaBook, FaBookOpen, FaGraduationCap, FaFlask, FaInfoCircle,
  FaLandmark, FaPenNib, FaAtom, FaChalkboardTeacher, FaUniversity,
  FaMicroscope, FaGlobe, FaLightbulb, FaFeatherAlt, FaScroll,
  FaLanguage, FaBrain, FaQuoteRight, FaCommentDots,
  FaChalkboard, FaBookmark, FaCertificate, FaAward, FaIdCard, FaIndustry, FaTools,
  // Society & current affairs
  FaNewspaper, FaChartLine, FaHeart, FaUserSecret, FaPoll,
  FaBalanceScale, FaGavel, FaComments, FaGlobeAmericas, FaBullhorn,
  FaVoteYea, FaHandshake, FaUsers, FaFistRaised, FaBriefcase,
  FaHandsHelping, FaUserFriends, FaUserTie, FaUsersCog, FaCity, FaFlagCheckered,
  // Lifestyle
  FaTshirt, FaUtensils, FaHeartbeat, FaPalette, FaPlane,
  FaCar, FaSun, FaCoffee, FaPaw, FaShoppingBag,
  FaHome, FaBaby, FaChild, FaRing, FaGlassCheers,
  FaHiking, FaUmbrellaBeach, FaBed, FaSpa, FaLeaf,
  FaShoePrints, FaSocks, FaGlasses, FaGift, FaBirthdayCake, FaCocktail,
  FaWineGlassAlt, FaPizzaSlice, FaIceCream, FaAppleAlt, FaCarrot,
  FaBreadSlice, FaCheese, FaDrumstickBite, FaHotdog,
  // Tech & science
  FaMicrochip, FaLaptopCode, FaRobot, FaMobileAlt, FaWifi, FaSatellite,
  FaServer, FaDatabase, FaCloud, FaRocket, FaCode, FaBug, FaBatteryFull, FaPlug,
  FaTabletAlt, FaDesktop, FaKeyboard, FaPrint, FaNetworkWired,
  FaShieldAlt, FaFingerprint, FaQrcode, FaBarcode,
  // Sports
  FaFutbol, FaBasketballBall, FaVolleyballBall, FaDumbbell,
  FaRunning, FaTableTennis, FaSwimmer, FaBicycle,
  FaBaseballBall, FaFootballBall, FaHockeyPuck, FaGolfBall,
  FaSkiing, FaHorse, FaMedal, FaTrophy,
  FaBowlingBall, FaSkating, FaSnowboarding,
  // Nature & animals
  FaDog, FaCat, FaFish, FaDove, FaTree, FaMountain, FaWater, FaSeedling,
  FaCloudSun, FaCloudRain, FaSnowflake, FaRainbow, FaGlobeAsia, FaGlobeEurope,
  // General / misc
  FaHashtag, FaQuestion, FaThumbtack, FaFire, FaBolt,
  FaCrown, FaGem, FaMapMarkerAlt, FaCalendarAlt, FaClock,
  FaExclamation, FaCheck, FaTimes, FaLock, FaUnlock,
  FaEye, FaBell, FaFlag, FaArrowUp, FaThumbsUp,
  FaMoneyBillWave, FaCoins, FaBuilding,
  FaShare, FaDownload, FaUpload, FaSyncAlt, FaSearch, FaFilter,
  FaCog, FaWrench, FaToolbox, FaBoxOpen, FaTag, FaTags, FaPaperPlane,
} from "react-icons/fa";

// The component type accepted by react-icons
type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

/** One browsable section of the icon picker grid. */
export interface TopicCategoryIconGroup {
  label: string; // Turkish label shown as a section header in the picker
  keys: string[];
}

/**
 * Icons grouped by theme for the admin icon picker.
 * The flat TOPIC_CATEGORY_ICON_MAP / TOPIC_CATEGORY_ICON_KEYS below are derived
 * from this so the two never drift out of sync.
 */
export const TOPIC_CATEGORY_ICON_GROUPS: TopicCategoryIconGroup[] = [
  {
    label: "Eğlence",
    keys: ["FaFilm", "FaTv", "FaMusic", "FaGamepad", "FaCameraRetro", "FaTheaterMasks",
           "FaMicrophoneAlt", "FaCompactDisc", "FaHeadphones", "FaStar",
           "FaPlayCircle", "FaVideo", "FaTicketAlt", "FaDice", "FaPuzzlePiece",
           "FaYoutube", "FaSpotify", "FaMagic", "FaGhost", "FaMask", "FaPaintBrush",
           "FaGuitar", "FaDrum", "FaChess"],
  },
  {
    label: "Bilgi & Kültür",
    keys: ["FaBook", "FaBookOpen", "FaGraduationCap", "FaFlask", "FaInfoCircle",
           "FaLandmark", "FaPenNib", "FaAtom", "FaChalkboardTeacher", "FaUniversity",
           "FaMicroscope", "FaGlobe", "FaLightbulb", "FaFeatherAlt", "FaScroll",
           "FaLanguage", "FaBrain", "FaQuoteRight", "FaCommentDots",
           "FaChalkboard", "FaBookmark", "FaCertificate", "FaAward", "FaIdCard",
           "FaIndustry", "FaTools"],
  },
  {
    label: "Toplum & Gündem",
    keys: ["FaNewspaper", "FaChartLine", "FaHeart", "FaUserSecret", "FaPoll",
           "FaBalanceScale", "FaGavel", "FaComments", "FaGlobeAmericas", "FaBullhorn",
           "FaVoteYea", "FaHandshake", "FaUsers", "FaFistRaised", "FaBriefcase",
           "FaHandsHelping", "FaUserFriends", "FaUserTie", "FaUsersCog", "FaCity",
           "FaFlagCheckered"],
  },
  {
    label: "Yaşam Tarzı",
    keys: ["FaTshirt", "FaUtensils", "FaHeartbeat", "FaPalette", "FaPlane",
           "FaCar", "FaSun", "FaCoffee", "FaPaw", "FaShoppingBag",
           "FaHome", "FaBaby", "FaChild", "FaRing", "FaGlassCheers",
           "FaHiking", "FaUmbrellaBeach", "FaBed", "FaSpa", "FaLeaf",
           "FaShoePrints", "FaSocks", "FaGlasses", "FaGift", "FaBirthdayCake",
           "FaCocktail", "FaWineGlassAlt", "FaPizzaSlice", "FaIceCream", "FaAppleAlt",
           "FaCarrot", "FaBreadSlice", "FaCheese", "FaDrumstickBite", "FaHotdog"],
  },
  {
    label: "Teknoloji & Bilim",
    keys: ["FaMicrochip", "FaLaptopCode", "FaRobot", "FaMobileAlt", "FaWifi", "FaSatellite",
           "FaServer", "FaDatabase", "FaCloud", "FaRocket", "FaCode", "FaBug",
           "FaBatteryFull", "FaPlug", "FaTabletAlt", "FaDesktop", "FaKeyboard",
           "FaPrint", "FaNetworkWired", "FaShieldAlt", "FaFingerprint", "FaQrcode", "FaBarcode"],
  },
  {
    label: "Spor",
    keys: ["FaFutbol", "FaBasketballBall", "FaVolleyballBall", "FaDumbbell",
           "FaRunning", "FaTableTennis", "FaSwimmer", "FaBicycle",
           "FaBaseballBall", "FaFootballBall", "FaHockeyPuck", "FaGolfBall",
           "FaSkiing", "FaHorse", "FaMedal", "FaTrophy",
           "FaBowlingBall", "FaSkating", "FaSnowboarding"],
  },
  {
    label: "Doğa & Hayvanlar",
    keys: ["FaDog", "FaCat", "FaFish", "FaDove", "FaTree", "FaMountain", "FaWater", "FaSeedling",
           "FaCloudSun", "FaCloudRain", "FaSnowflake", "FaRainbow",
           "FaGlobeAsia", "FaGlobeEurope"],
  },
  {
    label: "Genel",
    keys: ["FaHashtag", "FaQuestion", "FaThumbtack", "FaFire", "FaBolt",
           "FaCrown", "FaGem", "FaMapMarkerAlt", "FaCalendarAlt", "FaClock",
           "FaExclamation", "FaCheck", "FaTimes", "FaLock", "FaUnlock",
           "FaEye", "FaBell", "FaFlag", "FaArrowUp", "FaThumbsUp",
           "FaMoneyBillWave", "FaCoins", "FaBuilding",
           "FaShare", "FaDownload", "FaUpload", "FaSyncAlt", "FaSearch", "FaFilter",
           "FaCog", "FaWrench", "FaToolbox", "FaBoxOpen", "FaTag", "FaTags", "FaPaperPlane"],
  },
];

/** Static map from iconKey string → react-icons component. */
export const TOPIC_CATEGORY_ICON_MAP: Record<string, IconComponent> = {
  FaFilm, FaTv, FaMusic, FaGamepad, FaCameraRetro, FaTheaterMasks,
  FaMicrophoneAlt, FaCompactDisc, FaHeadphones, FaStar,
  FaPlayCircle, FaVideo, FaTicketAlt, FaDice, FaPuzzlePiece,
  FaYoutube, FaSpotify, FaMagic, FaGhost, FaMask, FaPaintBrush, FaGuitar, FaDrum, FaChess,
  FaBook, FaBookOpen, FaGraduationCap, FaFlask, FaInfoCircle,
  FaLandmark, FaPenNib, FaAtom, FaChalkboardTeacher, FaUniversity,
  FaMicroscope, FaGlobe, FaLightbulb, FaFeatherAlt, FaScroll,
  FaLanguage, FaBrain, FaQuoteRight, FaCommentDots,
  FaChalkboard, FaBookmark, FaCertificate, FaAward, FaIdCard, FaIndustry, FaTools,
  FaNewspaper, FaChartLine, FaHeart, FaUserSecret, FaPoll,
  FaBalanceScale, FaGavel, FaComments, FaGlobeAmericas, FaBullhorn,
  FaVoteYea, FaHandshake, FaUsers, FaFistRaised, FaBriefcase,
  FaHandsHelping, FaUserFriends, FaUserTie, FaUsersCog, FaCity, FaFlagCheckered,
  FaTshirt, FaUtensils, FaHeartbeat, FaPalette, FaPlane,
  FaCar, FaSun, FaCoffee, FaPaw, FaShoppingBag,
  FaHome, FaBaby, FaChild, FaRing, FaGlassCheers,
  FaHiking, FaUmbrellaBeach, FaBed, FaSpa, FaLeaf,
  FaShoePrints, FaSocks, FaGlasses, FaGift, FaBirthdayCake, FaCocktail,
  FaWineGlassAlt, FaPizzaSlice, FaIceCream, FaAppleAlt, FaCarrot,
  FaBreadSlice, FaCheese, FaDrumstickBite, FaHotdog,
  FaMicrochip, FaLaptopCode, FaRobot, FaMobileAlt, FaWifi, FaSatellite,
  FaServer, FaDatabase, FaCloud, FaRocket, FaCode, FaBug, FaBatteryFull, FaPlug,
  FaTabletAlt, FaDesktop, FaKeyboard, FaPrint, FaNetworkWired,
  FaShieldAlt, FaFingerprint, FaQrcode, FaBarcode,
  FaFutbol, FaBasketballBall, FaVolleyballBall, FaDumbbell,
  FaRunning, FaTableTennis, FaSwimmer, FaBicycle,
  FaBaseballBall, FaFootballBall, FaHockeyPuck, FaGolfBall,
  FaSkiing, FaHorse, FaMedal, FaTrophy,
  FaBowlingBall, FaSkating, FaSnowboarding,
  FaDog, FaCat, FaFish, FaDove, FaTree, FaMountain, FaWater, FaSeedling,
  FaCloudSun, FaCloudRain, FaSnowflake, FaRainbow, FaGlobeAsia, FaGlobeEurope,
  FaHashtag, FaQuestion, FaThumbtack, FaFire, FaBolt,
  FaCrown, FaGem, FaMapMarkerAlt, FaCalendarAlt, FaClock,
  FaExclamation, FaCheck, FaTimes, FaLock, FaUnlock,
  FaEye, FaBell, FaFlag, FaArrowUp, FaThumbsUp,
  FaMoneyBillWave, FaCoins, FaBuilding,
  FaShare, FaDownload, FaUpload, FaSyncAlt, FaSearch, FaFilter,
  FaCog, FaWrench, FaToolbox, FaBoxOpen, FaTag, FaTags, FaPaperPlane,
};

/** All available icon keys for the admin icon picker (flat list, ungrouped). */
export const TOPIC_CATEGORY_ICON_KEYS = Object.keys(TOPIC_CATEGORY_ICON_MAP);

/**
 * Suggested default icon per known channel name, used only to pre-select a
 * sensible icon the first time an admin opens the picker for that channel —
 * the admin can always change it. Not exhaustive; channels not listed here
 * simply start with no icon pre-selected.
 */
export const TOPIC_CATEGORY_SUGGESTED_ICON: Record<string, string> = {
  "3k1p": "FaHashtag",
  "anket": "FaPoll",
  "başı-boşlar": "FaQuestion",
  "bilgi": "FaInfoCircle",
  "bilim": "FaFlask",
  "dizi": "FaTv",
  "edebiyat": "FaPenNib",
  "eğitim": "FaGraduationCap",
  "ekonomi": "FaChartLine",
  "film": "FaFilm",
  "gastronomi": "FaUtensils",
  "haber": "FaNewspaper",
  "ilişkiler": "FaHeart",
  "kitap": "FaBook",
  "magazin": "FaCameraRetro",
  "moda": "FaTshirt",
  "müzik": "FaMusic",
  "nick-altı": "FaUserSecret",
  "otomotiv": "FaCar",
  "oyun": "FaGamepad",
  "sağlık": "FaHeartbeat",
  "sanat": "FaPalette",
  "seyahat": "FaPlane",
  "sinema": "FaFilm",
  "siyaset": "FaBalanceScale",
  "spor": "FaFutbol",
  "tarih": "FaLandmark",
  "tv": "FaTv",
  "teknoloji": "FaMicrochip",
  "yaşam": "FaSun",
};

/**
 * Render the icon for a given iconKey string.
 * Returns null when the key is not found (caller decides what "no icon" looks like).
 */
export const TopicCategoryIconByKey: React.FC<{ iconKey: string; size?: number; className?: string }> = ({
  iconKey,
  size = 14,
  className,
}) => {
  const Icon = TOPIC_CATEGORY_ICON_MAP[iconKey];
  if (!Icon) return null;
  return React.createElement(Icon, { size, className });
};